import {
  CancellationDestination,
  CancellationInclude,
  CancellationIncludeKey,
  ICancellationPreview,
  ICancellationRequest,
  ICancellationSubmit
} from "../../interfaces/reservations/Cancellation.types";
import { IReservationV2 } from "../../interfaces/reservations/Reservations.types";

export interface IIncludeItem {
  key: CancellationIncludeKey;
  label: string;
  /** Preview item products that mean this flag is in the refund. */
  products: string[];
}

export const INCLUDE_ITEMS: IIncludeItem[] = [
  { key: "serviceFee", label: "Tarifa e shërbimit", products: ["serviceFee"] },
  { key: "flexFee", label: "Flex", products: ["flexFee"] },
  { key: "smsFee", label: "Njoftimet me SMS", products: ["smsFee"] },
  { key: "travelSafe", label: "Travel Safe", products: ["travelSafe"] },
  { key: "luggage", label: "Ruajtja e bagazhit", products: ["luggage"] },
  { key: "openReturn", label: "Kthimi i hapur", products: ["openReturn"] }
];

type BookingExtras = Pick<
  IReservationV2,
  "flexType" | "hasTextMeMyTrip" | "conciergeName" | "hasLuggage" | "hasOpenReturn"
>;

// Only the items this booking actually bought get a toggle.
export const offeredIncludes = (booking: BookingExtras): IIncludeItem[] =>
  INCLUDE_ITEMS.filter(({ key }) => {
    if (key === "flexFee") return !!booking.flexType && booking.flexType !== "NotRefundable";
    if (key === "smsFee") return !!booking.hasTextMeMyTrip;
    if (key === "travelSafe") return !!booking.conciergeName;
    if (key === "luggage") return !!booking.hasLuggage;
    if (key === "openReturn") return !!booking.hasOpenReturn;
    return true;
  });

// The add-ons the server says the selected tickets carry; the booking's own until it answers.
export const includeChoices = (
  booking: BookingExtras,
  preview: ICancellationPreview | null
): IIncludeItem[] => {
  const addOns = preview?.addOns;
  return addOns ? INCLUDE_ITEMS.filter((item) => addOns.some((a) => item.products.indexOf(a.product) >= 0))
    : offeredIncludes(booking);
};

const findAddOn = (item: IIncludeItem, preview: ICancellationPreview | null) =>
  preview?.addOns?.find((a) => item.products.indexOf(a.product) >= 0);

// What the add-on adds to the refund, as the server priced it; null before it answers.
export const addOnValue = (item: IIncludeItem, preview: ICancellationPreview | null): number | null =>
  findAddOn(item, preview)?.value ?? null;

// An explicit choice wins; otherwise the toggle shows what the server's policy put in the preview,
// and before it answers, the admin default: everything ticked.
export const isIncluded = (
  item: IIncludeItem,
  overrides: CancellationInclude,
  preview: ICancellationPreview | null
): boolean => {
  const explicit = overrides[item.key] ?? findAddOn(item, preview)?.included;
  if (explicit !== undefined) return explicit;
  if (!preview) return true;
  return preview.items.some((line) => item.products.indexOf(line.product) >= 0);
};

export interface ICancellationSelection {
  uniqueJourneyId: string;
  ticketIds: number[];
  overrides: CancellationInclude;
  destination: CancellationDestination;
  reason: string;
  /** False: cancel and return nothing. Omitted means true. */
  refund?: boolean;
}

export const buildCancellationRequest = (s: ICancellationSelection): ICancellationRequest => {
  const refund = s.refund !== false;
  const include: CancellationInclude = {};
  if (refund)
    INCLUDE_ITEMS.forEach(({ key }) => {
      const value = s.overrides[key];
      if (value !== undefined) include[key] = value;
    });
  return {
    uniqueJourneyId: s.uniqueJourneyId,
    ticketIds: [...s.ticketIds].sort((a, b) => a - b),
    include,
    destination: s.destination,
    reason: s.reason.trim(),
    ...(refund ? {} : { refund: false })
  };
};

// The reason does not change the price, so typing it must not re-ask the server.
export const previewKey = (request: ICancellationRequest): string =>
  JSON.stringify({ ...request, reason: undefined });

export const buildCancellationSubmit = (
  request: ICancellationRequest,
  preview: ICancellationPreview,
  payoutNow: boolean
): ICancellationSubmit => ({
  ...request,
  quoteVersion: preview.quoteVersion,
  ...(payoutNow && preview.toCard > 0 ? { payoutNow: true } : {})
});

type ErrorLike = { response?: { status?: number; data?: unknown } };

// The refusal code the backend puts in errors.code (TravelCreditsController convention).
export const errorCode = (err: unknown): string | null => {
  const data = (err as ErrorLike)?.response?.data as
    | { errors?: { code?: unknown }; code?: unknown }
    | undefined;
  const fromErrors = data?.errors?.code;
  if (Array.isArray(fromErrors) && typeof fromErrors[0] === "string") return fromErrors[0];
  if (typeof fromErrors === "string") return fromErrors;
  return typeof data?.code === "string" ? data.code : null;
};

export const isQuoteStale = (err: unknown): boolean =>
  (err as ErrorLike)?.response?.status === 409 && errorCode(err) === "quote.stale";
